import { LoaderFunctionArgs } from '@remix-run/node';
import Layout from '~/components/layout';
import { validateUser } from '~/services/api/auth';
import { getCategoryList } from '~/services/api/categories';
import { Footer } from 'pdfs/components/footer';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;
  const selectedCategory = url.searchParams.get('category');

  const { user } = await validateUser(request);
  const categories = await getCategoryList(limit, page);

  return typedjson({
    categories,
    user,
    selectedCategory,
  });
};

const PrivacyPolicy = () => {
  const data = useTypedLoaderData<typeof loader>();

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <div className="container mx-auto mb-20 gap-4 px-4 sm:px-6 xl:px-56">
        <div className="flex flex-col items-center justify-center py-4">
          <h1 className="text-4xl">Privacy Policy</h1>
        </div>
        <div className="flex flex-col gap-2 pt-2">
          <p className="font-semibold">Effective Date: 10 October 2024</p>
          <p>
            This Privacy Policy describes how Best2bid ("we", "our", or "us") collects, uses, and protects your personal
            data in accordance with the Personal Data Protection Act 2010 of Malaysia ("PDPA"). By accessing or using
            our platform, you consent to the processing of your personal data as described in this policy.
          </p>
        </div>
        <div className="border-grey-100 mb-2 border-b py-4" />
        <div className="flex flex-col gap-4 pt-4">
          <p className="text-2xl">1. Collection of Personal Data</p>
          <p>We collect personal data when you:</p>
          <ul className="list-disc pl-6">
            <li>Register or create an account on our platform</li>
            <li>Participate in auctions or make purchases</li>
            <li>Contact our support team or communicate with us</li>
            <li>Subscribe to newsletters, promotional content, or updates</li>
          </ul>
          <p>The personal data we may collect includes (but is not limited to):</p>
          <ul className="list-disc pl-6">
            <li>Full name</li>
            <li>Contact information (email, phone number, address)</li>
            <li>Identity verification documents (e.g., NRIC/passport, if required)</li>
            <li>Payment and transaction details</li>
            <li>IP address and device/browser information</li>
          </ul>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">2. Purpose of Collection</p>
          <p>Your personal data may be used for the following purposes:</p>
          <ul className="list-disc pl-6">
            <li>To provide you access to our platform and services</li>
            <li>To verify your identity for secure transactions</li>
            <li>To process and manage transactions, bids, and payments</li>
            <li>To communicate with you, including customer support and promotional updates</li>
            <li>To comply with legal and regulatory requirements</li>
            <li>To improve our platform functionality and user experience</li>
          </ul>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">3. Disclosure of Personal Data</p>
          <p>We do not sell or rent your personal data. However, we may disclose your data:</p>
          <ul className="list-disc pl-6">
            <li>To merchants, auction houses, or sellers for transaction fulfillment</li>
            <li>To third-party service providers (e.g., payment gateways, hosting services)</li>
            <li>When required by law or regulatory authorities</li>
            <li>In connection with any legal proceedings or potential legal claims</li>
          </ul>
          <p>All such disclosures are made in accordance with the PDPA and on a need-to-know basis.</p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">4. Data Protection & Security</p>
          <p>
            We implement appropriate technical and organizational security measures to safeguard your personal data
            against unauthorized access, disclosure, or misuse.
          </p>
          <p>
            However, while we strive to protect your personal information, no system is 100% secure. You are encouraged
            to keep your login credentials confidential and log out after each session.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">5. Access and Correction</p>
          <p>
            You have the right to access and correct your personal data held by us. To make a request, please contact us
            at:
          </p>
          <ul className="list-disc pl-6">
            <li>Email: admin@best2bid.com </li>
            <li>Phone: 016-419 3313</li>
          </ul>
          <p>We may charge a reasonable administrative fee for data access requests.</p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">6. Retention of Data</p>
          <p>
            Your personal data will be retained for as long as necessary to fulfill the purposes it was collected for,
            or as required by applicable laws and regulations.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">7. Cookies</p>
          <p>
            Our website uses cookies to enhance your user experience. You may disable cookies through your browser
            settings, but some features of our platform may not function properly as a result.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">8. Third-Party Links</p>
          <p>
            Our platform may contain links to external websites. This Privacy Policy applies only to Best2bid. We are
            not responsible for the privacy practices of other sites.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">9. Policy Updates</p>
          <p>
            We may update this Privacy Policy from time to time. The updated version will be posted on our platform with
            the effective date. We encourage you to review it periodically.
          </p>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">10. Contact Us</p>
          <p>If you have any questions regarding this Privacy Policy or your personal data, please contact:</p>
          <ul className="list-disc pl-6">
            <li>Best2bid Privacy Officer</li>
            <li>Email: admin@best2bid.com</li>
            <li>Address: 192A, Jalan Jelutong, 11600 Penang, Malaysia</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
